// src/pages/public/Home.jsx
import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  TrendingUp, 
  Shield, 
  Users, 
  ArrowRight,
  Star,
  CheckCircle,
  Award,
  Zap,
  BarChart3,
  Clock,
  Globe,
  Building,
  Target,
  Rocket
} from 'lucide-react'

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0)

  const heroSlides = [
    {
      title: "Build Your Future",
      subtitle: "Strategic investments for long-term wealth creation",
      image: "🏛️",
      gradient: "from-[#561530] to-[#811844]"
    },
    {
      title: "Elite Returns",
      subtitle: "Premium investment opportunities with exceptional returns",
      image: "📈",
      gradient: "from-[#811844] to-[#9E1C60]"
    },
    {
      title: "Secure Growth",
      subtitle: "Military-grade security for your financial future",
      image: "🛡️",
      gradient: "from-[#9E1C60] to-[#F5AD18]"
    }
  ]

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  const stats = [
    { number: "12K+", label: "Active Investors", icon: Users },
    { number: "$2.5B+", label: "Capital Deployed", icon: TrendingUp },
    { number: "1.5%", label: "Avg. ROI", icon: BarChart3 },
    { number: "99.9%", label: "Uptime", icon: Shield }
  ]

  const features = [
    {
      icon: Building,
      title: "Institutional Grade",
      description: "Access institutional-level investment opportunities with premium returns"
    },
    {
      icon: Target,
      title: "Strategic Focus",
      description: "Carefully curated investment strategies for optimal growth"
    },
    {
      icon: Globe,
      title: "Global Reach",
      description: "Diversified portfolio with international market exposure"
    },
    {
      icon: Clock,
      title: "Long-term Vision",
      description: "Sustainable investment approach focused on future growth"
    }
  ]

  const investmentTiers = [
    {
      name: "Foundation",
      min: "$1,000",
      roi: "4x",
      period: "Monthly",
      features: ["Basic Portfolio", "Market Analysis", "Email Support"],
      color: "border-[#561530]"
    },
    {
      name: "Growth",
      min: "$1,500",
      roi: "4.25x",
      period: "Monthly",
      features: ["Advanced Portfolio", "Priority Support", "Risk Management"],
      color: "border-[#9E1C60]",
      popular: true
    },
    {
      name: "Elite",
      min: "$2,000",
      roi: "4.50x",
      period: "Monthly",
      features: ["VIP Portfolio", "Dedicated Manager", "Exclusive Opportunities"],
      color: "border-[#F5AD18]"
    }
  ]

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Classic Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#561530]/5 to-[#F5AD18]/5"></div>
        
        {/* Animated Slides */}
        <div className="absolute inset-0">
          {heroSlides.map((slide, index) => (
            <motion.div
              key={index}
              className={`absolute inset-0 bg-gradient-to-br ${slide.gradient} transition-opacity duration-1000`}
              initial={{ opacity: 0 }}
              animate={{ opacity: currentSlide === index ? 1 : 0 }}
              transition={{ duration: 1 }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="block">TGFuture</span>
              <span className="text-3xl md:text-5xl font-light opacity-90">Wealth Management</span>
            </h1>
            
            <p className="text-xl md:text-2xl mb-8 opacity-90 max-w-2xl mx-auto leading-relaxed">
              Building lasting wealth through strategic investments and innovative financial solutions
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="https://workplace.tgfuture.com/register"
                className="bg-[#F5AD18] hover:bg-[#F5AD18]/90 text-[#561530] px-8 py-4 rounded-lg font-semibold text-lg flex items-center gap-3 transition-all transform hover:scale-105 shadow-lg"
              >
                <Rocket className="w-5 h-5" />
                Start Your Journey
                <ArrowRight className="w-4 h-4" />
              </Link>
              
              <Link
                to="/about"
                className="border-2 border-white text-white hover:bg-white hover:text-[#561530] px-8 py-4 rounded-lg font-semibold text-lg transition-all"
              >
                Learn More
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border-2 border-white rounded-full flex justify-center"
          >
            <div className="w-1 h-3 bg-white rounded-full mt-2"></div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="bg-white dark:bg-gray-700 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-600">
                    <div className="w-12 h-12 bg-[#9E1C60] rounded-xl flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      {stat.number}
                    </div>
                    <div className="text-gray-600 dark:text-gray-400 text-sm">
                      {stat.label}
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Why Choose TGFuture?
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Experience institutional-grade investment management with personalized service
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group text-center"
              >
                <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 transition-all duration-300 group-hover:shadow-xl group-hover:border-[#F5AD18] border-2 border-transparent">
                  <div className="w-16 h-16 bg-gradient-to-r from-[#9E1C60] to-[#F5AD18] rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Tiers */}
      <section className="py-20 bg-gradient-to-br from-[#561530]/5 to-[#F5AD18]/5">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Investment Tiers
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Choose the investment level that matches your financial goals
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {investmentTiers.map((tier, index) => (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className={`relative bg-white dark:bg-gray-800 rounded-2xl p-8 border-2 ${tier.color} shadow-xl transition-all duration-300 hover:scale-105 ${
                  tier.popular ? 'ring-2 ring-[#F5AD18]' : ''
                }`}
              >
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-[#F5AD18] text-[#561530] px-4 py-1 rounded-full text-sm font-bold">
                      Most Popular
                    </div>
                  </div>
                )}
                
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                  {tier.name}
                </h3>
                
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold text-[#9E1C60] mb-2">{tier.roi}</div>
                  <div className="text-gray-600 dark:text-gray-400">Expected Returns</div>
                </div>

                <div className="text-center mb-6">
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">Minimum</div>
                  <div className="text-2xl font-bold text-[#F5AD18]">{tier.min}</div>
                </div>

                <div className="space-y-3 mb-8">
                  {tier.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-[#9E1C60] flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="https://workplace.tgfuture.com/register"
                  className={`w-full py-3 rounded-lg font-semibold text-center block transition-all ${
                    tier.popular
                      ? 'bg-[#F5AD18] hover:bg-[#F5AD18]/90 text-[#561530]'
                      : 'bg-[#561530] hover:bg-[#811844] text-white'
                  }`}
                >
                  Get Started
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#561530] text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-bold mb-6">
              Ready to Build Your Future?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Join thousands of investors who trust TGFuture with their financial growth
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="https://workplace.tgfuture.com/register"
                className="bg-[#F5AD18] hover:bg-[#F5AD18]/90 text-[#561530] px-8 py-4 rounded-lg font-semibold text-lg flex items-center gap-3 transition-all transform hover:scale-105"
              >
                <TrendingUp className="w-5 h-5" />
                Start Investing Today
              </Link>
              
              <Link
                to="/contact"
                className="border-2 border-white text-white hover:bg-white hover:text-[#561530] px-8 py-4 rounded-lg font-semibold text-lg transition-all"
              >
                Contact Our Advisors
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Home